import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiFetch } from "@/lib/auth";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import {
    Check,
    Lock,
    PartyPopper,
    Copy,
    Twitter,
    MessageCircle,
    Mail,
    ArrowRight,
    Trophy,
    Store as StoreIcon,
    Package,
    Megaphone,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";

const STEP_ICONS = [StoreIcon, Package, Megaphone];

export default function MerchantOnboarding() {
    const [data, setData] = useState(null);
    const [celebrating, setCelebrating] = useState(false);
    const reduce = useReducedMotion();

    const load = async () => {
        const { data } = await apiFetch.get("/merchant/onboarding");
        setData(data);
    };

    useEffect(() => {
        load();
    }, []);

    if (!data) {
        return <div className="h-64 rounded-xl bg-zinc-100 animate-pulse" />;
    }

    const shareUrl =
        data.shareUrl
            ? `${window.location.origin}${data.shareUrl}`
            : null;

    const onShare = async () => {
        if (!shareUrl) return;
        try {
            await navigator.clipboard.writeText(shareUrl);
            toast.success("Enlace copiado", {
                description: "Pégalo donde quieras compartirlo.",
            });
        } catch {
            // ignore
        }
        await apiFetch.post("/merchant/onboarding/share");
        const before = data.complete;
        await load();
        if (!before && !reduce) {
            setCelebrating(true);
            setTimeout(() => setCelebrating(false), 4500);
        } else if (!before && reduce) {
            // Respect reduced-motion: announce success without confetti.
            toast.success("¡Onboarding completado!", {
                description: "Tu tienda está lista para vender.",
            });
        }
    };

    const progressPct = (data.progress / data.total) * 100;

    return (
        <div data-testid="merchant-onboarding" className="space-y-8">
            <header>
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">
                    Onboarding gamificado
                </p>
                <h1 className="mt-2 text-3xl sm:text-4xl font-bold tracking-tight text-zinc-900">
                    Tu camino a la primera venta
                </h1>
                <p className="mt-3 text-sm text-zinc-600 max-w-xl">
                    Tres pasos. Sin prisa, sin código. Cada paso completado te
                    acerca a tu primer pedido.
                </p>
                <div className="mt-6 flex items-center gap-4 max-w-md">
                    <Progress value={progressPct} className="flex-1 h-2" />
                    <span className="text-sm font-semibold text-zinc-700">
                        {data.progress}/{data.total}
                    </span>
                </div>
            </header>

            {data.complete ? (
                <CompletedCard shareUrl={shareUrl} onShare={onShare} />
            ) : null}

            <ol className="space-y-4" data-testid="onboarding-steps">
                {data.steps.map((step, i) => (
                    <StepCard
                        key={step.id}
                        index={i}
                        step={step}
                        shareUrl={shareUrl}
                        onShare={onShare}
                    />
                ))}
            </ol>

            <AnimatePresence>
                {celebrating && <Confetti />}
            </AnimatePresence>
        </div>
    );
}

function StepCard({ step, index, shareUrl, onShare }) {
    const reduce = useReducedMotion();
    const num = String(index + 1).padStart(2, "0");
    const locked = step.locked && !step.done;
    const isShare = step.id === "share";
    const Icon = STEP_ICONS[index] || StoreIcon;

    return (
        <motion.li
            initial={reduce ? false : { opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: reduce ? 0 : 0.4, delay: reduce ? 0 : index * 0.1 }}
            data-testid={`onboarding-step-${step.id}`}
            className={`relative rounded-2xl border p-6 transition-all ${
                step.done
                    ? "border-success/30 bg-success-subtle/40"
                    : locked
                    ? "border-zinc-200 bg-zinc-50/40 opacity-70"
                    : "border-zinc-200 bg-white hover:shadow-sm"
            }`}
        >
            <div className="flex items-start gap-5">
                <div
                    aria-hidden="true"
                    className={`w-12 h-12 rounded-xl grid place-items-center shrink-0 ${
                        step.done
                            ? "bg-success text-success-foreground"
                            : locked
                            ? "bg-zinc-100 text-zinc-500"
                            : "bg-zinc-900 text-white"
                    }`}
                >
                    {step.done ? (
                        <Check className="w-6 h-6" />
                    ) : locked ? (
                        <Lock className="w-5 h-5" />
                    ) : (
                        <Icon className="w-6 h-6" />
                    )}
                </div>
                <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold uppercase tracking-[0.15em] text-zinc-600">
                        Paso {num}
                    </p>
                    <h3 className="mt-1 text-lg font-semibold text-zinc-900">
                        {step.title}
                    </h3>
                    <p className="mt-1 text-sm text-zinc-600 leading-relaxed">
                        {step.body}
                    </p>

                    {!step.done && !locked && (
                        <div className="mt-4">
                            {isShare ? (
                                <ShareActions
                                    shareUrl={shareUrl}
                                    onShare={onShare}
                                />
                            ) : (
                                <Button
                                    asChild
                                    data-testid={`onboarding-step-cta-${step.id}`}
                                    className="bg-primary hover:bg-primary-hover text-primary-foreground"
                                >
                                    <Link to={step.ctaTo}>
                                        {step.ctaLabel}
                                        <ArrowRight className="w-4 h-4 ml-1" aria-hidden="true" />
                                    </Link>
                                </Button>
                            )}
                        </div>
                    )}

                    {step.done && (
                        <p className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-success">
                            <Check className="w-3.5 h-3.5" aria-hidden="true" /> Completado
                        </p>
                    )}
                </div>
            </div>
        </motion.li>
    );
}

function ShareActions({ shareUrl, onShare }) {
    if (!shareUrl) return null;
    const text = encodeURIComponent(
        "Mira mi tienda en Tiendify 🛍️ — productos hechos en México. " + shareUrl
    );
    return (
        <div className="space-y-3">
            <div className="flex items-center gap-2 max-w-md">
                <code className="flex-1 px-3 py-2 rounded-md bg-zinc-100 text-xs text-zinc-700 truncate font-mono">
                    {shareUrl}
                </code>
                <Button
                    type="button"
                    onClick={onShare}
                    data-testid="onboarding-step-cta-share"
                    className="bg-primary hover:bg-primary-hover text-primary-foreground"
                >
                    <Copy className="w-4 h-4 mr-1" aria-hidden="true" />
                    Copiar
                </Button>
            </div>
            <div className="flex items-center gap-2">
                <SocialBtn
                    href={`https://wa.me/?text=${text}`}
                    label="WhatsApp"
                    icon={MessageCircle}
                />
                <SocialBtn
                    href={`https://twitter.com/intent/tweet?text=${text}`}
                    label="X"
                    icon={Twitter}
                />
                <SocialBtn
                    href={`mailto:?subject=Mi tienda en Tiendify&body=${text}`}
                    label="Correo"
                    icon={Mail}
                />
            </div>
        </div>
    );
}

function SocialBtn({ href, label, icon: Icon }) {
    return (
        <a
            href={href}
            target="_blank"
            rel="noreferrer"
            aria-label={`Compartir por ${label}`}
            className="inline-flex items-center gap-1.5 px-4 h-11 rounded-md border border-zinc-200 hover:border-zinc-300 hover:bg-zinc-50 text-sm font-medium text-zinc-700 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
        >
            <Icon className="w-4 h-4" aria-hidden="true" />
            {label}
        </a>
    );
}

function CompletedCard({ shareUrl, onShare }) {
    return (
        <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            data-testid="onboarding-complete"
            className="relative overflow-hidden rounded-2xl bg-zinc-900 text-white p-8"
        >
            <div aria-hidden="true" className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-primary/30 blur-3xl" />
            <div aria-hidden="true" className="absolute -bottom-10 -left-10 w-40 h-40 rounded-full bg-warning/20 blur-3xl" />
            <div className="relative">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 backdrop-blur text-xs font-semibold">
                    <Trophy className="w-3.5 h-3.5 text-amber-300" aria-hidden="true" />
                    Onboarding completado
                </div>
                <h2 className="mt-4 text-2xl sm:text-3xl font-bold tracking-tight">
                    ¡Felicidades! Tu tienda está lista para vender 🚀
                </h2>
                <p className="mt-2 text-sm text-zinc-300 max-w-lg">
                    Comparte tu link, atiende los primeros pedidos y nosotros nos
                    encargamos del envío. Estás 3-4× más cerca de tu primera venta
                    que quien no completa el onboarding.
                </p>
                {shareUrl && (
                    <div className="mt-5 flex items-center gap-2 max-w-md">
                        <code className="flex-1 px-3 py-2 rounded-md bg-white/10 text-xs truncate font-mono">
                            {shareUrl}
                        </code>
                        <Button
                            type="button"
                            onClick={onShare}
                            className="bg-white text-zinc-900 hover:bg-zinc-100"
                        >
                            <Copy className="w-4 h-4 mr-1" />
                            Copiar
                        </Button>
                    </div>
                )}
            </div>
        </motion.div>
    );
}

function Confetti() {
    const pieces = Array.from({ length: 60 });
    // CSS variables resolved at render so they follow the active theme.
    const colors = ["#4F46E5", "#F59E0B", "#10B981", "#EF4444", "#0A0A0A"];
    return (
        <motion.div
            initial={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 pointer-events-none z-[60] overflow-hidden"
            data-testid="onboarding-confetti"
            aria-hidden="true"
        >
            <div className="absolute inset-0 grid place-items-center">
                <motion.div
                    initial={{ scale: 0, rotate: -10 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{ type: "spring", stiffness: 220 }}
                    className="bg-white rounded-2xl shadow-2xl px-8 py-6 flex items-center gap-4 border border-zinc-200"
                >
                    <PartyPopper className="w-8 h-8 text-warning" />
                    <div>
                        <p className="text-xs font-semibold uppercase tracking-wider text-primary">
                            ¡Listo!
                        </p>
                        <p className="text-base font-semibold text-zinc-900">
                            Tu tienda está lista para vender
                        </p>
                    </div>
                </motion.div>
            </div>
            {pieces.map((_, i) => {
                const left = Math.random() * 100;
                const delay = Math.random() * 0.6;
                const dur = 2 + Math.random() * 1.5;
                const color = colors[i % colors.length];
                const rotate = Math.random() * 720;
                return (
                    <motion.span
                        key={i}
                        initial={{ y: -40, opacity: 0, rotate: 0 }}
                        animate={{
                            y: typeof window !== "undefined" ? window.innerHeight + 40 : 800,
                            opacity: [0, 1, 1, 0],
                            rotate,
                        }}
                        transition={{
                            duration: dur,
                            delay,
                            ease: "easeIn",
                        }}
                        style={{
                            position: "absolute",
                            left: `${left}%`,
                            width: 8,
                            height: 12,
                            borderRadius: 2,
                            background: color,
                        }}
                    />
                );
            })}
        </motion.div>
    );
}
